import { useEffect, useState } from "react";
import { Plus, Trash2, Edit3, X, Save } from "lucide-react";
import AdminHeader from "../../components/layout/AdminHeader";
import { api, formatApiError } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { toast } from "sonner";

const empty = {
  company_name: "", partner_type: "developer", contact_person: "", email: "", phone: "",
  website: "", country: "", cities: [], logo_url: "", description: "",
  services: [], internal_notes: "", relationship_status: "active",
  verification_status: "unverified", is_public: false,
};

export default function AdminPartners() {
  const settings = useSettings();
  const partnerTypes = settings?.get("partner_type") || [];
  const [filterType, setFilterType] = useState("");
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // partner or empty

  const load = async () => {
    const q = new URLSearchParams(); if (filterType) q.set("partner_type", filterType);
    const { data } = await api.get(`/partners?${q}`);
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType]);

  const save = async () => {
    try {
      if (editing.id) await api.patch(`/partners/${editing.id}`, editing);
      else await api.post("/partners", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.company_name}"?`)) return;
    await api.delete(`/partners/${p.id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="page-admin-partners">
      <AdminHeader breadcrumb="Relationships" title="Strategic Partnerships"
        subtitle="Developers, brokerage, legal, financial, tour, marketing, service providers, and strategic partners powering Altura's global operations."
        actions={<button onClick={() => setEditing({ ...empty })} className="btn-gold" data-testid="partner-new"><Plus className="h-3.5 w-3.5" /> New partner</button>} />
      <div className="p-8 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="partner-type-filters">
          <button onClick={() => setFilterType("")} className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition ${!filterType ? "bg-altura-espresso text-altura-ivory" : "border border-altura-espresso/15 text-altura-espresso hover:border-altura-gold"}`}>All</button>
          {partnerTypes.map((t) => (
            <button key={t.key} onClick={() => setFilterType(t.key)}
                    className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition ${filterType === t.key ? "bg-altura-espresso text-altura-ivory" : "border border-altura-espresso/15 text-altura-espresso hover:border-altura-gold"}`}
                    data-testid={`partner-filter-${t.key}`}>{t.label}</button>
          ))}
        </div>

        <div className="admin-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-[0.15em] text-altura-charcoal/50">
              <tr className="border-b border-altura-espresso/10">
                <th className="text-left py-3 px-2">Company</th><th className="text-left py-3 px-2">Type</th>
                <th className="text-left py-3 px-2">Country</th><th className="text-left py-3 px-2">Contact</th>
                <th className="text-left py-3 px-2">Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-b border-altura-espresso/5" data-testid={`partner-row-${p.id}`}>
                  <td className="py-3 px-2 font-medium text-altura-espresso">{p.company_name}</td>
                  <td className="py-3 px-2"><span className="badge-soft">{partnerTypes.find((x) => x.key === p.partner_type)?.label || p.partner_type}</span></td>
                  <td className="py-3 px-2">{p.country || "—"}</td>
                  <td className="py-3 px-2 text-xs text-altura-charcoal/70">{p.contact_person || "—"}{p.email ? ` · ${p.email}` : ""}</td>
                  <td className="py-3 px-2 capitalize">{p.relationship_status}</td>
                  <td className="py-3 px-2">
                    <button onClick={() => setEditing({ ...p })} className="text-altura-espresso/60 hover:text-altura-gold-dark mr-3" data-testid={`partner-edit-${p.id}`}><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => del(p)} className="text-altura-espresso/60 hover:text-altura-burgundy" data-testid={`partner-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan="6" className="py-10 text-center text-altura-charcoal/50">No partners in this category yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-altura-espresso/60 p-4 overflow-y-auto" onClick={() => setEditing(null)} data-testid="partner-modal">
          <div className="w-full max-w-2xl rounded-lg bg-white p-8 mt-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-editorial text-2xl text-altura-espresso">{editing.id ? "Edit partner" : "New partner"}</div>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Company name</label><input className="admin-input mt-1" value={editing.company_name} onChange={(e) => setEditing({ ...editing, company_name: e.target.value })} data-testid="partner-company" /></div>
              <div><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Partner type</label>
                <select className="admin-select mt-1" value={editing.partner_type} onChange={(e) => setEditing({ ...editing, partner_type: e.target.value })} data-testid="partner-type">
                  {partnerTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Contact person</label><input className="admin-input mt-1" value={editing.contact_person || ""} onChange={(e) => setEditing({ ...editing, contact_person: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Country</label><input className="admin-input mt-1" value={editing.country || ""} onChange={(e) => setEditing({ ...editing, country: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Email</label><input className="admin-input mt-1" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Phone</label><input className="admin-input mt-1" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Website</label><input className="admin-input mt-1" value={editing.website || ""} onChange={(e) => setEditing({ ...editing, website: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Description</label><textarea rows={3} className="admin-input mt-1" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Internal notes (private)</label><textarea rows={2} className="admin-input mt-1" value={editing.internal_notes || ""} onChange={(e) => setEditing({ ...editing, internal_notes: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Relationship status</label>
                <select className="admin-select mt-1" value={editing.relationship_status} onChange={(e) => setEditing({ ...editing, relationship_status: e.target.value })}>
                  <option value="prospect">Prospect</option><option value="active">Active</option><option value="paused">Paused</option><option value="inactive">Inactive</option>
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Verification</label>
                <select className="admin-select mt-1" value={editing.verification_status} onChange={(e) => setEditing({ ...editing, verification_status: e.target.value })}>
                  <option value="unverified">Unverified</option><option value="verified">Verified</option><option value="rejected">Rejected</option>
                </select>
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_public} onChange={(e) => setEditing({ ...editing, is_public: e.target.checked })} data-testid="partner-public" /> Approve for public directory display</label>
            </div>
            <div className="mt-8 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-ghost-espresso">Cancel</button>
              <button onClick={save} className="btn-gold" data-testid="partner-save"><Save className="h-3.5 w-3.5" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
